import { RefObject, useEffect } from "react";

const FOCUSABLE_SELECTOR =
  'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface UseFocusTrapOptions {
  /** When true, focus is trapped inside the container and Tab/Shift+Tab cycle within it. */
  active: boolean;
  /** Optional ref to focus when trap activates (e.g. close button). If not set, first focusable is used. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * Traps focus inside a container when active (e.g. modal/drawer open).
 * Tab cycles to first focusable; Shift+Tab from first goes to last.
 * Uses keydown + focusin fallback so focus is re-contained in Firefox when keydown preventDefault doesn't run in time.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  { active, initialFocusRef }: UseFocusTrapOptions,
): void {
  useEffect(() => {
    if (!active || !containerRef.current) {
      return;
    }
    const container = containerRef.current;

    const getFocusable = (): HTMLElement[] => {
      return Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null,
      );
    };

    if (initialFocusRef?.current) {
      initialFocusRef.current.focus({ preventScroll: true });
    } else {
      const focusable = getFocusable();
      focusable[0]?.focus({ preventScroll: true });
    }

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== "Tab") {
        return;
      }
      const focusable = getFocusable();
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;
      const activeIndex = activeEl ? focusable.indexOf(activeEl) : -1;
      const isInside = activeIndex >= 0;
      let target: HTMLElement | undefined;
      if (!isInside) {
        target = e.shiftKey ? last : first;
      } else if (e.shiftKey && activeEl === first) {
        target = last;
      } else if (!e.shiftKey && activeEl === last) {
        target = first;
      }
      if (target !== undefined) {
        e.preventDefault();
        target.focus();
      }
    };

    /** Fallback for Firefox: when focus leaves the trap, redirect back. */
    const handleFocusIn = (e: FocusEvent): void => {
      const target = e.target as Node | null;
      if (target && container.contains(target)) {
        return;
      }
      const focusable = getFocusable();
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0]!;
      requestAnimationFrame(() => {
        if (
          containerRef.current &&
          !containerRef.current.contains(document.activeElement)
        ) {
          first.focus();
        }
      });
    };

    container.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("focusin", handleFocusIn, true);
    return () => {
      container.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("focusin", handleFocusIn, true);
    };
  }, [active, containerRef, initialFocusRef]);
}
